<script lang="ts" setup>
import {
  type Question,
  QuestionDisplayType,
  QuestionType,
  QuestionnaireType,
} from '@/ts/types/dto/interventions-dto';
import ReadOnlyMultichoice from '@/vue/molecules/read-only/questionnaire/read-only-multichoice.vue';
import ReadOnlyNumeric from '@/vue/molecules/read-only/questionnaire/read-only-numeric.vue';
import ReadOnlyNonCollapsable from '@/vue/molecules/read-only/helpers/read-only-non-collapsable.vue';
import ReadOnlyQuestionSlider from '@/vue/molecules/read-only/questionnaire/read-only-question-slider.vue';
import ReadOnlyGridMultichoice from '@/vue/molecules/read-only/questionnaire/read-only-grid-multichoice.vue';
import ReadOnlyDropdown from '@/vue/molecules/read-only/questionnaire/read-only-dropdown.vue';
import WeightLossCalculationDisplay from '@/vue/molecules/components/question/weight-loss-calculation-display.vue';
import {computed} from 'vue';

const props = defineProps<{
  question: Question;
  questionnaireType?: string;
  currentWeightGrams?: number | null;
}>();

const isWeightGoalQuestionnaire = computed(() => {
  return props.questionnaireType === QuestionnaireType.WEIGHT_LOSS_PERCENT;
});

// The backend sends null (not []) for an unanswered multichoice question, so normalize
// here once before fanning the answers out to the read-only display components (IPD-1079).
const selectedIds = computed(() => props.question.type === QuestionType.MULTICHOICE
  ? props.question.multiChoiceSelectedIds ?? []
  : []);

const selectedPercentage = computed(() => {
  if (props.question.type === QuestionType.MULTICHOICE) {
    if (!selectedIds.value.length || !props.question.multiChoice?.options) {
      return null;
    }
    const selectedOption = props.question.multiChoice.options.find(
      option => option.id === selectedIds.value[0],
    );
    return selectedOption?.value ?? null;
  } else if (props.question.type === QuestionType.NUMERIC) {
    return props.question.numericSelected ?? null;
  }
  return null;
});

const currentWeight = computed(() => props.currentWeightGrams ?? null);
</script>

<template>
  <read-only-non-collapsable active class="read-only-question">
    <b v-safe-html="question.name"/>
    <template v-if="question.type === QuestionType.MULTICHOICE">
      <read-only-question-slider
        v-if="question.displayType === QuestionDisplayType.HORIZONTAL_SLIDER"
        :question="question.multiChoice"
        :answers="selectedIds"
      />
      <read-only-grid-multichoice
        v-else-if="question.displayType === QuestionDisplayType.GRID"
        :question="question.multiChoice"
        :answers="selectedIds"
      />
      <read-only-multichoice
        v-else-if="question.displayType === QuestionDisplayType.CHECKBOX_WITH_INPUT"
        :question="question.multiChoice"
        :answers="selectedIds"
        :custom-answer="question.customAnswer"
      />
      <read-only-dropdown
        v-else-if="question.displayType === QuestionDisplayType.DROPDOWN"
        :question="question.multiChoice"
        :answers="selectedIds"
        :custom-answer="question.customAnswer"
      />
      <read-only-multichoice
        v-else
        :question="question.multiChoice"
        :answers="selectedIds"
      />
    </template>
    <read-only-numeric
      v-else-if="question.type === QuestionType.NUMERIC"
      :answer="question.numericSelected"
      :question="question"
    />
    <weight-loss-calculation-display
      v-if="isWeightGoalQuestionnaire"
      :selected-percentage="selectedPercentage"
      :current-weight="currentWeight"
    />
  </read-only-non-collapsable>
</template>
<style lang="scss" scoped>
.read-only-question {
  display: flex;
  flex-direction: column;
  gap: var(--Spacing-spacing-3);
}
</style>
